import { useCallback, useEffect, useRef, useState } from 'react';

import { getRolesByCorrespondentId } from '@shared/services/api/rolesService';

const rolesCache = new Map();
const inFlightRequests = new Map();

function dedupeRolesById(dataSet) {
  if (!Array.isArray(dataSet)) {
    return [];
  }
  const seen = new Set();
  const result = [];
  for (const item of dataSet) {
    if (seen.has(item.id)) {
      continue;
    }
    seen.add(item.id);
    result.push(item);
  }
  return result;
}

async function requestRoles(correspondentId) {
  const response = await getRolesByCorrespondentId(correspondentId);
  const dataSet = response?.data?.data?.data_set;
  if (!Array.isArray(dataSet)) {
    throw new Error('Respuesta de roles con formato inesperado');
  }
  return dedupeRolesById(dataSet);
}

export function fetchCorrespondentRoles(correspondentId, { force = false } = {}) {
  if (correspondentId === undefined || correspondentId === null || correspondentId === '') {
    return Promise.resolve([]);
  }
  const key = String(correspondentId);

  if (!force && rolesCache.has(key)) {
    return Promise.resolve(rolesCache.get(key));
  }
  if (!force && inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const promise = requestRoles(correspondentId)
    .then((roles) => {
      rolesCache.set(key, roles);
      return roles;
    })
    .finally(() => {
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, promise);
  return promise;
}

export function prefetchCorrespondentRoles(correspondentId) {
  return fetchCorrespondentRoles(correspondentId).catch(() => []);
}

export function clearCorrespondentRolesCache(correspondentId) {
  if (correspondentId === undefined) {
    rolesCache.clear();
    inFlightRequests.clear();
    return;
  }
  const key = String(correspondentId);
  rolesCache.delete(key);
  inFlightRequests.delete(key);
}

export function useCorrespondentRoles(correspondentId) {
  const key =
    correspondentId === undefined || correspondentId === null || correspondentId === ''
      ? null
      : String(correspondentId);
  const cached = key ? rolesCache.get(key) : undefined;

  const [roles, setRoles] = useState(cached ?? []);
  const [loading, setLoading] = useState(!!key && !cached);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (!key) {
        setRoles([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchCorrespondentRoles(correspondentId, { force });
        if (!mounted.current) return;
        setRoles(data);
      } catch (err) {
        console.error('[useCorrespondentRoles]', err);
        if (!mounted.current) return;
        setRoles([]);
        setError(err);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [correspondentId, key]
  );

  useEffect(() => {
    if (!key) {
      setRoles([]);
      setLoading(false);
      return;
    }
    const fromCache = rolesCache.get(key);
    if (fromCache) {
      setRoles(fromCache);
      setLoading(false);
      setError(null);
      return;
    }
    load();
  }, [key, load]);

  const reload = useCallback(() => load({ force: true }), [load]);

  return { roles, loading, error, reload };
}

export default useCorrespondentRoles;
